<script setup>
import BaseText from "@/library/BaseText.vue";
import LineProgress from "@/features/ui/LineProgress.vue";
import InlineSvg from "@/features/InlineSvg.vue";
import { onBeforeUnmount, onMounted, ref } from "vue";

defineProps({
  step: {
    type: Number,
    required: true,
  },
  steps: {
    type: Array,
    default: () => [],
  },
});

defineEmits(["back"]);

const header = ref(null);
const headerWidth = ref(0);

const onWindowResize = () => {
  headerWidth.value = header.value?.offsetWidth ?? 0;
};

onMounted(() => {
  window.addEventListener("resize", onWindowResize, { passive: true });
  onWindowResize();
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", onWindowResize);
});
</script>

<template>
  <div
    ref="header"
    class="enrollment-header"
  >
    <span class="enrollment-header__logo">
      <InlineSvg name="cloaked-logo-orange" />
    </span>
    <LineProgress
      :width="headerWidth"
      :progress="step / steps.length"
      class="enrollment-header__progress"
    />
    <button
      class="enrollment-header__back"
      :disabled="$route.meta.step === 1"
      @click="$emit('back')"
      @keydown.enter="$emit('back')"
    >
      <InlineSvg
        name="chevron-left"
        class="enrollment-header__back-icon"
      />
    </button>
    <BaseText
      variant="caption-semibold"
      class="enrollment-header__caption"
    >
      Step {{ step }} of {{ steps.length }}
    </BaseText>
    <BaseText
      as="h1"
      variant="headline-6-medium"
      class="enrollment-header__title"
    >
      {{ steps[step - 1] }}
    </BaseText>
  </div>
</template>

<style scoped lang="scss">
.enrollment-header {
  display: grid;
  gap: 2px 8px;
  grid-template-columns: 24px 1fr;
  grid-template-areas: "logo logo" "progress progress" "back caption" "back title";

  &__logo {
    grid-area: logo;
    place-self: center;
    margin-bottom: 16px;
    width: 30px;
    height: 30px;

    & > * {
      width: 100%;
      height: 100%;
    }

    :deep(path:first-child) {
      fill: $color-primary-100;
    }
  }

  &__progress {
    grid-area: progress;
    margin-bottom: 20px;
  }

  &__back {
    grid-area: back;
    place-self: center;
    cursor: pointer;
    background: none;
    border: none;
    padding: 0;
    width: 24px;
    height: 24px;

    &:hover {
      opacity: 0.7;
    }

    &:disabled {
      opacity: 1;
      cursor: not-allowed;
    }

    &-icon {
      width: 19px;
      height: 19px;
    }

    &:focus-visible {
      outline: 1px solid $color-primary-100;
    }
  }

  &__caption {
    grid-area: caption;
  }

  &__title {
    grid-area: title;
  }
}
</style>
